// HR / HRMS-admin pages: leave admin, HR desk, reports, settings, audit log.
// Sub-views live in ?view= so the quick-actions menu can link straight to them.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Checkbox, FormControlLabel, InputAdornment, MenuItem, TextField } from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { fileUrl, fmtDate, inr, thisMonth, WEEKDAYS, REQUEST_CATEGORIES, EMPLOYMENT_TYPES, COLORS } from "./api";
import {
  useLoad,
  useAction,
  Loading,
  Card,
  Pill,
  Tag,
  Stat,
  Empty,
  Person,
  DateBadge,
  Segmented,
  ConfirmDialog,
  FormDialog,
  MonthNav,
  YearNav,
  BarChart,
  HBars,
} from "./shared";
import { AttendanceTable } from "./Team";
import styles from "./Hrms.module.css";

const tf = { size: "small", fullWidth: true };
const btn = { textTransform: "none", fontWeight: 700, fontFamily: "inherit", borderRadius: "10px", boxShadow: "none" };

// ?view= for a tab with sub-views.
function useView(tab, fallback) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || fallback;
  return [view, (v) => setParams({ tab, view: v })];
}

function CsvButton({ href }) {
  return (
    <Button variant="outlined" size="small" href={href} startIcon={<FileDownloadOutlinedIcon />} sx={btn}>
      CSV
    </Button>
  );
}

// -------------------------------------------------------------- leave admin

function LeaveRequests() {
  const [status, setStatus] = useState("pending");
  const [month, setMonth] = useState("");
  const { data, error, reload } = useLoad(`/leave-requests?status=${status}&month=${month}`);
  const [cancelling, setCancelling] = useState(null);
  const { run } = useAction();
  return (
    <Card
      title="Leave requests"
      icon={<FactCheckOutlinedIcon />}
      subtitle="Approve or reject from Approvals. Here HR can also cancel leave that was already approved."
      actions={<TextField size="small" type="month" label="Month" InputLabelProps={{ shrink: true }} value={month} onChange={(e) => setMonth(e.target.value)} />}
    >
      <div className={styles.toolbar}>
        <Segmented
          value={status}
          onChange={setStatus}
          options={["pending", "approved", "rejected", "cancelled", "all"].map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))}
        />
      </div>
      {!data ? (
        <Loading error={error} />
      ) : data.requests.length === 0 ? (
        <Empty icon={<FactCheckOutlinedIcon />} text="No requests." />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Dates</th>
                <th>Type</th>
                <th>Days</th>
                <th>Status</th>
                <th>Decided by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.requests.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Person name={r.employee?.name || "—"} size={30} />
                  </td>
                  <td>
                    {fmtDate(r.start_date)}
                    {r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ""}
                  </td>
                  <td>{r.leave_type}</td>
                  <td>
                    <strong>{Number(r.days)}</strong>
                  </td>
                  <td>
                    <Pill status={r.status} />
                  </td>
                  <td className={styles.small}>{r.decided_by ? `${r.decided_by.split("@")[0]} · ${fmtDate(r.decided_at)}` : `Waiting on ${r.approver}`}</td>
                  <td className={styles.rowActions}>
                    {["pending", "approved"].includes(r.status) && (
                      <Button size="small" color="error" onClick={() => setCancelling(r)} sx={btn}>
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(cancelling)}
        title="Cancel this leave?"
        confirmLabel="Cancel leave"
        danger
        onClose={() => setCancelling(null)}
        onConfirm={() => {
          const r = cancelling;
          setCancelling(null);
          run(`/leave-requests/${r.id}/cancel`, { method: "POST", body: {} }, reload);
        }}
      >
        {cancelling && (
          <p>
            {cancelling.employee?.name}: {cancelling.leave_type}, {fmtDate(cancelling.start_date)}
            {cancelling.end_date !== cancelling.start_date ? ` – ${fmtDate(cancelling.end_date)}` : ""}. The days go back to their balance.
          </p>
        )}
      </ConfirmDialog>
    </Card>
  );
}

const EMPTY_TYPE = { code: "", name: "", annual_quota: "", accrual: "yearly", paid: true, allow_half_day: true, active: true };

function LeaveTypes() {
  const { data, error, reload } = useLoad("/leave-types");
  const [form, setForm] = useState(null); // null = closed; EMPTY_TYPE = adding
  const [editing, setEditing] = useState(null);
  const { busy, run } = useAction();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const close = () => {
    setEditing(null);
    setForm(null);
  };
  if (!data) return <Loading error={error} />;
  return (
    <Card
      title="Leave policy"
      icon={<TuneOutlinedIcon />}
      subtitle="Yearly: full quota up front (pro-rated for new joiners). Monthly: a twelfth each month."
      actions={
        <Button className={styles.primaryBtn} variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setForm(EMPTY_TYPE)}>
          Add leave type
        </Button>
      }
    >
      <div className={styles.balances}>
        {data.leaveTypes.map((t, i) => (
          <div key={t.id} className={`${styles.balance} ${styles[`c_${COLORS[i % COLORS.length]}`]}`} style={{ opacity: t.active ? 1 : 0.55 }}>
            <div className={styles.balanceTop}>
              <span className={styles.balanceName}>{t.name}</span>
              <span className={styles.balanceCode}>{t.code}</span>
            </div>
            <div className={styles.balanceValue}>
              {t.annual_quota == null ? "∞" : Number(t.annual_quota)}
              <small>{t.annual_quota == null ? "no limit" : "days / year"}</small>
            </div>
            <div className={styles.inline} style={{ marginTop: 8 }}>
              <Tag>{t.accrual === "monthly" ? "Monthly accrual" : "Yearly"}</Tag>
              <Tag>{t.paid ? "Paid" : "Unpaid"}</Tag>
              {t.allow_half_day && <Tag>Half days</Tag>}
              {!t.active && <Tag color="red">Inactive</Tag>}
            </div>
            <Button
              size="small"
              startIcon={<EditOutlinedIcon />}
              onClick={() => {
                setEditing(t.id);
                setForm({ ...t, annual_quota: t.annual_quota == null ? "" : Number(t.annual_quota) });
              }}
              sx={{ ...btn, mt: 1, ml: -1 }}
            >
              Edit
            </Button>
          </div>
        ))}
      </div>
      <FormDialog
        open={Boolean(form)}
        onClose={close}
        title={editing ? `Edit ${form && form.code}` : "Add a leave type"}
        icon={<TuneOutlinedIcon />}
        actions={
          <Button
            className={styles.primaryBtn}
            variant="contained"
            disabled={busy || !form || !form.code || !form.name}
            onClick={() =>
              run(editing ? `/leave-types/${editing}` : "/leave-types", { method: editing ? "PUT" : "POST", body: form }, () => {
                close();
                reload();
              })
            }
          >
            Save
          </Button>
        }
      >
        {form && (
          <>
            <div className={styles.formRow} style={{ marginTop: 8 }}>
              <TextField {...tf} label="Code" value={form.code} onChange={set("code")} helperText="e.g. CL" />
              <TextField {...tf} label="Name" value={form.name} onChange={set("name")} />
              <TextField {...tf} type="number" label="Days per year" value={form.annual_quota} onChange={set("annual_quota")} helperText="Empty = unlimited" />
              <TextField {...tf} select label="Accrual" value={form.accrual} onChange={set("accrual")}>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
            </div>
            <div className={styles.inline}>
              {[
                ["paid", "Paid"],
                ["allow_half_day", "Half days allowed"],
                ["active", "Active"],
              ].map(([k, l]) => (
                <FormControlLabel key={k} control={<Checkbox size="small" checked={Boolean(form[k])} onChange={set(k)} />} label={<span style={{ fontSize: 14 }}>{l}</span>} />
              ))}
            </div>
          </>
        )}
      </FormDialog>
    </Card>
  );
}

function Holidays({ me }) {
  const [year, setYear] = useState(Number(me.today.slice(0, 4)));
  const { data, error, reload } = useLoad(`/holidays?year=${year}`);
  const [form, setForm] = useState({ date: "", name: "", optional: false });
  const { busy, run } = useAction();
  return (
    <Card
      title="Holidays"
      icon={<CelebrationOutlinedIcon />}
      color="pink"
      subtitle={`Weekly offs: ${me.settings.weekly_offs.map((d) => WEEKDAYS[d]).join(", ") || "none"} (change in Settings). Optional holidays still count as working days.`}
      actions={<YearNav year={year} onChange={setYear} />}
    >
      <div className={styles.formRow} style={{ gridTemplateColumns: "180px minmax(180px, 1fr) auto auto", alignItems: "center" }}>
        <TextField {...tf} type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <TextField {...tf} label="Holiday name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <FormControlLabel control={<Checkbox size="small" checked={form.optional} onChange={(e) => setForm({ ...form, optional: e.target.checked })} />} label={<span style={{ fontSize: 14 }}>Optional</span>} />
        <Button
          className={styles.primaryBtn}
          variant="contained"
          startIcon={<AddRoundedIcon />}
          disabled={busy || !form.date || !form.name}
          onClick={() =>
            run("/holidays", { method: "POST", body: form }, () => {
              setForm({ date: "", name: "", optional: false });
              reload();
            })
          }
        >
          Add
        </Button>
      </div>
      {!data ? (
        <Loading error={error} />
      ) : data.holidays.length === 0 ? (
        <Empty icon={<CelebrationOutlinedIcon />} text={`No holidays for ${year} yet.`} />
      ) : (
        <div className={styles.peopleGrid}>
          {data.holidays.map((h) => (
            <div key={h.id} className={styles.personCard} style={{ flexDirection: "row", alignItems: "center" }}>
              <DateBadge date={h.date} color="pink" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={styles.personName}>{h.name}</div>
                <div className={styles.personSub}>
                  {new Date(`${h.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long" })}
                  {h.optional ? " · optional" : ""}
                </div>
              </div>
              <Button size="small" color="error" onClick={() => run(`/holidays/${h.id}`, { method: "DELETE" }, reload)} sx={btn}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function LeaveAdmin({ me }) {
  const [view, setView] = useView("leave_admin", "requests");
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "requests", label: "Leave requests", icon: <FactCheckOutlinedIcon /> },
            { value: "policy", label: "Leave policy", icon: <TuneOutlinedIcon /> },
            { value: "holidays", label: "Holidays", icon: <CelebrationOutlinedIcon /> },
          ]}
        />
      </div>
      {view === "requests" && <LeaveRequests />}
      {view === "policy" && <LeaveTypes />}
      {view === "holidays" && <Holidays me={me} />}
    </>
  );
}

// ------------------------------------------------------------------ HR desk

const requestLabel = (c) => (REQUEST_CATEGORIES[c] || c).split(" (")[0];

function DeskItem({ r, onSaved }) {
  const [response, setResponse] = useState(r.response || "");
  const { busy, run } = useAction();
  const save = (status) => run(`/hr-requests/${r.id}`, { method: "PUT", body: { status, response } }, onSaved);
  const color = r.status === "resolved" ? "green" : r.status === "in_progress" ? "blue" : "amber";
  return (
    <div className={`${styles.reqCard} ${styles[`c_${color}`]}`} style={{ alignItems: "start" }}>
      <div>
        <Person name={r.employee?.name || "—"} sub={`Sent ${fmtDate(r.createdAt)}`} size={40} />
        <div className={styles.inline} style={{ marginTop: 8 }}>
          <Tag color="teal">{requestLabel(r.category)}</Tag>
          <Pill status={r.status} />
        </div>
      </div>
      <div>
        <div className={styles.listTitle}>{r.subject}</div>
        {r.body && <div className={styles.muted} style={{ marginTop: 2, whiteSpace: "pre-wrap" }}>{r.body}</div>}
      </div>
      <div style={{ width: 300, maxWidth: "100%" }}>
        <TextField size="small" fullWidth multiline minRows={2} placeholder="Reply to the employee" value={response} onChange={(e) => setResponse(e.target.value)} />
        <div className={styles.actions} style={{ marginTop: 8, justifyContent: "flex-end" }}>
          {r.status === "open" && (
            <Button size="small" disabled={busy} onClick={() => save("in_progress")} sx={btn}>
              Mark in progress
            </Button>
          )}
          <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => save("resolved")} sx={btn}>
            {r.status === "resolved" ? "Update reply" : "Resolve"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function HrDesk({ onChanged }) {
  const [status, setStatus] = useState("open");
  const { data, error, reload } = useLoad(`/hr-requests?status=${status}`);
  const saved = () => {
    reload();
    onChanged();
  };
  return (
    <Card title="Help desk" icon={<SupportAgentOutlinedIcon />} color="teal" subtitle="Letters, documents, salary and access requests from employees.">
      <div className={styles.toolbar}>
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { value: "open", label: "Open" },
            { value: "in_progress", label: "In progress" },
            { value: "resolved", label: "Resolved" },
            { value: "all", label: "All" },
          ]}
        />
      </div>
      {!data ? (
        <Loading error={error} />
      ) : data.requests.length === 0 ? (
        <Empty icon={<SupportAgentOutlinedIcon />} title="Nothing here" text="New requests from employees show up in Open." />
      ) : (
        data.requests.map((r) => <DeskItem key={`${r.id}-${r.updatedAt}`} r={r} onSaved={saved} />)
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ reports

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function SummaryReport({ me }) {
  const [year, setYear] = useState(Number(me.today.slice(0, 4)));
  const { data, error } = useLoad(`/reports/summary?year=${year}`);
  if (!data) return <Loading error={error} />;
  const types = Object.entries(data.byEmploymentType)
    .map(([k, value]) => ({ label: EMPLOYMENT_TYPES[k] || k, value }))
    .sort((a, b) => b.value - a.value);
  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.cardTitle}>Headcount, hiring & attrition</div>
        <YearNav year={year} onChange={setYear} />
      </div>
      <div className={styles.tiles}>
        <Stat label="Employees" value={data.total} tone="violet" icon={<GroupsOutlinedIcon />} />
        <Stat label="Onboarding" value={data.byStatus.onboarding || 0} tone="teal" icon={<RocketLaunchOutlinedIcon />} />
        <Stat label="Serving notice" value={data.byStatus.notice || 0} tone="warn" icon={<HourglassEmptyOutlinedIcon />} />
        <Stat label="Joined" sub={`in ${year}`} value={data.joinedInYear} tone="good" icon={<PersonAddAlt1OutlinedIcon />} />
        <Stat label="Left" sub={`in ${year}`} value={data.exitedInYear} tone="bad" icon={<PersonRemoveOutlinedIcon />} />
        <Stat label="Attrition" value={`${data.attritionRate}%`} sub="of avg. headcount" tone="pink" icon={<TrendingDownOutlinedIcon />} />
      </div>
      <div className={styles.gridMain}>
        <Card title="Headcount at month end" icon={<InsightsOutlinedIcon />}>
          <BarChart
            points={data.months.map((m, i) => ({
              label: `${MONTHS[i]} ${year}`,
              short: MONTHS[i],
              value: m.headcount || 0,
              detail: m.headcount == null ? "not yet" : `+${m.joined} joined, −${m.exited} left`,
            }))}
            format={(v) => `${v} people`}
          />
        </Card>
        <Card title="By employment type" icon={<GroupsOutlinedIcon />} color="teal">
          {types.length ? <HBars rows={types} /> : <Empty text="No employees yet." />}
        </Card>
      </div>
      <Card title="Month by month" icon={<TodayOutlinedIcon />} color="slate">
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th />
                {MONTHS.map((m) => (
                  <th key={m}>{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["Joined", "joined", "good"],
                ["Left", "exited", "bad"],
                ["Headcount", "headcount", null],
              ].map(([l, k, tone]) => (
                <tr key={k}>
                  <td>
                    <strong>{l}</strong>
                  </td>
                  {data.months.map((m) => (
                    <td key={m.month} className={tone && m[k] ? styles[`num_${tone}`] : undefined}>
                      {m[k] == null ? "—" : m[k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function AttendanceReport({ me }) {
  const [month, setMonth] = useState(thisMonth(me.today));
  const { data, error } = useLoad(`/reports/attendance?month=${month}`);
  return (
    <Card
      title="Attendance report"
      icon={<TodayOutlinedIcon />}
      color="green"
      actions={
        <>
          <MonthNav month={month} onChange={setMonth} />
          <CsvButton href={fileUrl(`/reports/attendance?month=${month}&format=csv`)} />
        </>
      }
    >
      {data ? <AttendanceTable rows={data.rows} /> : <Loading error={error} />}
    </Card>
  );
}

function LeaveReport({ me }) {
  const [year, setYear] = useState(Number(me.today.slice(0, 4)));
  const { data, error } = useLoad(`/reports/leave?year=${year}`);
  const types = data && data.rows[0] ? data.rows[0].balances : [];
  return (
    <Card
      title="Leave report"
      icon={<BeachAccessOutlinedIcon />}
      color="blue"
      subtitle="Used / left for each leave type."
      actions={
        <>
          <YearNav year={year} onChange={setYear} />
          <CsvButton href={fileUrl(`/reports/leave?year=${year}&format=csv`)} />
        </>
      }
    >
      {!data ? (
        <Loading error={error} />
      ) : data.rows.length === 0 ? (
        <Empty text="No one to show." />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Employee</th>
                {types.map((t) => (
                  <th key={t.leave_type_id}>{t.code}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employee_id}>
                  <td>
                    <Person name={r.name} sub={r.employee_code} size={30} />
                  </td>
                  {r.balances.map((b) => (
                    <td key={b.leave_type_id}>
                      <strong>{b.used}</strong>
                      <span className={styles.small}> / {b.unlimited ? "∞" : b.available} left</span>
                      {b.pending ? <div className={styles.num_warn} style={{ fontSize: 12 }}>+{b.pending} pending</div> : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SalaryReport() {
  const { data, error } = useLoad("/reports/salary");
  if (!data) return <Loading error={error} />;
  const withCtc = data.rows.reduce((a, r) => a + r.with_ctc, 0);
  const headcount = data.rows.reduce((a, r) => a + r.headcount, 0);
  return (
    <Card title="Salary cost" icon={<PaymentsOutlinedIcon />} color="green" subtitle="From the annual CTC on each profile. Payroll isn't run here; payslips are uploaded per employee.">
      <div className={styles.tiles}>
        <Stat label="Total annual CTC" value={inr(data.total_ctc)} tone="good" icon={<PaymentsOutlinedIcon />} />
        <Stat label="Monthly cost" value={inr(data.monthly_cost)} tone="info" icon={<TodayOutlinedIcon />} />
        <Stat label="Average CTC" value={withCtc ? inr(data.total_ctc / withCtc) : "—"} tone="violet" icon={<InsightsOutlinedIcon />} />
        <Stat
          label="Without CTC"
          value={`${data.missing_ctc} of ${headcount}`}
          tone={data.missing_ctc ? "warn" : "slate"}
          sub={data.missing_ctc ? "add it on their profile" : "everyone has one"}
          icon={<GroupsOutlinedIcon />}
        />
      </div>
    </Card>
  );
}

export function Reports({ me }) {
  const [view, setView] = useView("reports", "overview");
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "overview", label: "Headcount", icon: <InsightsOutlinedIcon /> },
            { value: "attendance", label: "Attendance", icon: <TodayOutlinedIcon /> },
            { value: "leave", label: "Leave", icon: <BeachAccessOutlinedIcon /> },
            { value: "salary", label: "Salary cost", icon: <PaymentsOutlinedIcon /> },
          ]}
        />
      </div>
      {view === "overview" && <SummaryReport me={me} />}
      {view === "attendance" && <AttendanceReport me={me} />}
      {view === "leave" && <LeaveReport me={me} />}
      {view === "salary" && <SalaryReport />}
    </>
  );
}

// ----------------------------------------------------------------- settings

function CompanySettings({ me, onChanged }) {
  const { data, error, reload } = useLoad("/settings");
  const [form, setForm] = useState(null);
  const { busy, run } = useAction();
  useEffect(() => {
    if (data) {
      const s = data.settings;
      setForm({ ...s, onboarding_checklist: s.onboarding_checklist.join("\n"), offboarding_checklist: s.offboarding_checklist.join("\n") });
    }
  }, [data]);
  if (!form) return <Loading error={error} />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleOff = (d) => setForm({ ...form, weekly_offs: form.weekly_offs.includes(d) ? form.weekly_offs.filter((x) => x !== d) : [...form.weekly_offs, d] });
  const readOnly = !me.isAdmin;
  const mins = { InputProps: { endAdornment: <InputAdornment position="end">min</InputAdornment> } };
  const save = () =>
    run(
      "/settings",
      {
        method: "PUT",
        body: {
          ...form,
          grace_minutes: Number(form.grace_minutes),
          full_day_minutes: Number(form.full_day_minutes),
          half_day_minutes: Number(form.half_day_minutes),
          leave_year_start_month: Number(form.leave_year_start_month),
          regularization_window_days: Number(form.regularization_window_days),
          onboarding_checklist: form.onboarding_checklist.split("\n"),
          offboarding_checklist: form.offboarding_checklist.split("\n"),
        },
      },
      () => {
        reload();
        onChanged();
      },
    );
  return (
    <>
      {readOnly && <div className={styles.infoBox}>Only HRMS admins can change these settings.</div>}
      <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
        <Card title="Company" icon={<ApartmentOutlinedIcon />}>
          <div className={styles.formRow}>
            <TextField {...tf} label="Company name" value={form.company_name} onChange={set("company_name")} />
            <TextField {...tf} label="Timezone" value={form.timezone} onChange={set("timezone")} helperText="e.g. Asia/Kolkata" />
            <TextField {...tf} label="Employee ID prefix" value={form.employee_code_prefix} onChange={set("employee_code_prefix")} helperText="IDs look like EMP-0001" />
            <TextField {...tf} select label="Leave year starts" value={form.leave_year_start_month} onChange={set("leave_year_start_month")}>
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </Card>
        <Card title="Office hours & attendance" icon={<ScheduleOutlinedIcon />} color="green" subtitle="Changing these also changes past attendance reports.">
          <div className={styles.formRow}>
            <TextField {...tf} type="time" label="Work starts" InputLabelProps={{ shrink: true }} value={form.work_start} onChange={set("work_start")} />
            <TextField {...tf} type="time" label="Work ends" InputLabelProps={{ shrink: true }} value={form.work_end} onChange={set("work_end")} />
            <TextField {...tf} type="number" label="Late after (grace)" value={form.grace_minutes} onChange={set("grace_minutes")} {...mins} />
            <TextField {...tf} type="number" label="Full day" value={form.full_day_minutes} onChange={set("full_day_minutes")} helperText="Overtime counts beyond this" {...mins} />
            <TextField {...tf} type="number" label="Half day" value={form.half_day_minutes} onChange={set("half_day_minutes")} helperText="Below this the day is absent" {...mins} />
            <TextField
              {...tf}
              type="number"
              label="Fix-a-day window"
              value={form.regularization_window_days}
              onChange={set("regularization_window_days")}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            />
            <TextField
              {...tf}
              type="date"
              label="Attendance tracked from"
              InputLabelProps={{ shrink: true }}
              value={form.attendance_start_date || ""}
              onChange={set("attendance_start_date")}
              helperText="Earlier days aren't counted as absent"
            />
          </div>
          <div className={styles.sectionTitle}>Weekly offs</div>
          <div className={styles.seg}>
            {WEEKDAYS.map((w, d) => (
              <button key={w} type="button" className={form.weekly_offs.includes(d) ? styles.segOn : ""} onClick={() => !readOnly && toggleOff(d)}>
                {w}
              </button>
            ))}
          </div>
        </Card>
        <Card title="Checklists" icon={<ChecklistRtlOutlinedIcon />} color="amber" subtitle="Added to each new joiner / leaver. One item per line.">
          <div className={styles.grid2} style={{ marginBottom: 0 }}>
            <TextField {...tf} multiline minRows={6} label="Onboarding" value={form.onboarding_checklist} onChange={set("onboarding_checklist")} />
            <TextField {...tf} multiline minRows={6} label="Offboarding" value={form.offboarding_checklist} onChange={set("offboarding_checklist")} />
          </div>
        </Card>
      </fieldset>
      {!readOnly && (
        <div className={styles.actions} style={{ justifyContent: "flex-end" }}>
          <Button className={styles.primaryBtn} variant="contained" disabled={busy} onClick={save}>
            Save settings
          </Button>
        </div>
      )}
    </>
  );
}

export function Settings({ me, onChanged }) {
  const [view, setView] = useView("settings", "company");
  return (
    <>
      {me.isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: "company", label: "Settings", icon: <TuneOutlinedIcon /> },
              { value: "audit", label: "Audit log", icon: <HistoryOutlinedIcon /> },
            ]}
          />
        </div>
      )}
      {me.isAdmin && view === "audit" ? <AuditLog /> : <CompanySettings me={me} onChanged={onChanged} />}
    </>
  );
}

// ---------------------------------------------------------------- audit log

function AuditLog() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const { data, error } = useLoad(`/audit?limit=300&q=${encodeURIComponent(query)}`);
  useEffect(() => {
    const t = setTimeout(() => setQuery(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Card
      title="Audit log"
      icon={<HistoryOutlinedIcon />}
      color="slate"
      subtitle="Every change in HRMS, newest first. Salary values are never recorded — only that the field changed."
      actions={
        <TextField
          size="small"
          placeholder="Filter by action or person"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      }
    >
      {!data ? (
        <Loading error={error} />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>On</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id}>
                  <td className={styles.small} style={{ whiteSpace: "nowrap" }}>
                    {new Date(e.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className={styles.small}>{e.actor_email}</td>
                  <td>
                    <Tag color="violet">{e.action}</Tag>
                  </td>
                  <td className={styles.small}>
                    {e.entity}
                    {e.entity_id ? ` #${e.entity_id}` : ""}
                  </td>
                  <td className={`${styles.small} ${styles.mono}`} style={{ maxWidth: 360, wordBreak: "break-word" }}>
                    {e.details ? JSON.stringify(e.details) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
